import { AlertContainer } from '@/components/ui';
import { UNAVAILABILITY_ICON } from '@/constants/professional/iconMap';
import { UNAVAILABILITY_TYPE_TRANSLATION } from '@/constants/professional/translations';
import { useModal } from '@/hooks';
import {
	useDeleteUnavailableDate,
	useUpdateUnavailableDate,
} from '@/services/mutations/professional-unavailability';
import { Pencil, Trash, Clock } from 'lucide-react'; // Adicionei Clock para o header
import { CreateUnavailabityForm } from './CreateUnavailabityForm';
import { formatDateForInput } from '@/utils';

const UnavailabilityCard = ({
	id,
	type,
	reason,
	start_date,
	end_date,
}: ProfessionalUnavailabilitys.ProfessionalUnavailability) => {
	const {
		openModal,
		setCancelText,
		setConfirmText,
		setOnConfirm,
		closeModal,
	} = useModal();

	const formatDateRange = (startDate: string, endDate: string): string => {
		const [startYear, startMonth, startDay] = startDate
			.split('T')[0]
			.split('-');
		const [endYear, endMonth, endDay] = endDate.split('T')[0].split('-');

		const monthNames = [
			'Jan',
			'Fev',
			'Mar',
			'Abr',
			'Mai',
			'Jun',
			'Jul',
			'Ago',
			'Set',
			'Out',
			'Nov',
			'Dez',
		];
		const startMonthName = monthNames[parseInt(startMonth) - 1];
		const endMonthName = monthNames[parseInt(endMonth) - 1];

		if (startMonth === endMonth && startYear === endYear) {
			if (startDay === endDay)
				return `${startMonthName} ${startDay}, ${startYear}`;
			return `${startMonthName} ${startDay}-${endDay}, ${startYear}`;
		}
		return `${startMonthName} ${startDay}, ${startYear} / ${endMonthName} ${endDay}, ${endYear}`;
	};

	const { mutate: deleteMutate } = useDeleteUnavailableDate();
	const { mutate: updateMutate } = useUpdateUnavailableDate(() => {
		closeModal();
	});

	const handleOnDelete = (id: string) => {
		setConfirmText('confirmar');
		setCancelText('Cancelar');

		const message = (
			<div className="text-stone-900">
				<p className="mb-2 text-base">
					Tem certeza que deseja deletar a indisponibilidade?
				</p>
				<div className="rounded-lg border border-stone-100 bg-stone-50 p-3 text-sm">
					<p>
						Data:{' '}
						<span className="font-bold text-red-600">
							{formatDateRange(start_date, end_date)}
						</span>
					</p>
					{reason && (
						<p>
							Descrição:{' '}
							<span className="font-bold text-red-600">
								{reason}
							</span>
						</p>
					)}
				</div>
			</div>
		);

		openModal({
			content: <AlertContainer message={message} />,
		});
		setOnConfirm(() => deleteMutate(id));
	};

	const handleOnEdit = () => {
		const onEdit = (data: ProfessionalUnavailabilitys.Create) => {
			const formattedStartDate = formatDateForInput(data.start_date);
			const formattedEndDate = formatDateForInput(data.end_date);

			updateMutate({
				id,
				start_date: formattedStartDate,
				end_date: formattedEndDate,
				reason: data.reason || '',
				type: data.type,
			});
		};

		openModal({
			title: 'Editar indisponibilidade',
			content: (
				<CreateUnavailabityForm
					onAction={onEdit}
					onCancel={closeModal} // <--- CORREÇÃO: Isso faz o botão "Voltar" funcionar no Modal
					defaultData={{
						id,
						type,
						start_date,
						end_date,
						reason,
					}}
				/>
			),
		});
	};

	return (
		<div className="animate-in fade-in w-full max-w-96 space-y-3 rounded-2xl border border-stone-200 bg-white p-4 shadow-sm duration-500">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex items-center gap-2 rounded-full bg-stone-100 px-3 py-1">
					<div className="scale-90 text-stone-600">
						{UNAVAILABILITY_ICON[type]}
					</div>
					<h6 className="text-xs font-bold uppercase tracking-tight text-stone-900">
						{UNAVAILABILITY_TYPE_TRANSLATION[type]}
					</h6>
				</div>

				<div className="flex items-center gap-1.5 text-stone-400">
					<Clock size={12} />
					<span className="text-[11px] font-bold">
						{formatDateRange(start_date, end_date)}
					</span>
				</div>
			</div>

			<div className="flex items-end justify-between gap-4">
				<div className="flex-1">
					{reason ? (
						<p className="line-clamp-2 text-sm italic leading-snug text-stone-600">
							"{reason}"
						</p>
					) : (
						<p className="text-xs italic text-stone-300">
							Sem descrição
						</p>
					)}
				</div>

				<div className="flex gap-1">
					<button
						className="rounded-xl p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-900"
						onClick={handleOnEdit}
					>
						<Pencil size={16} />
					</button>
					<button
						className="rounded-xl p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-red-500"
						onClick={() => handleOnDelete(id)}
					>
						<Trash size={16} />
					</button>
				</div>
			</div>
		</div>
	);
};

export { UnavailabilityCard };
